/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";

import {AuthenticationService} from './authentication.service';
import {BaseService} from "./base-service";

import {Point} from "../models/point";
import {PropertyPatch} from "../models/property-patch";
import {Reader} from '../models/reader';
import {ReaderInstance} from '../models/reader-instance';
import {Function} from '../models/function';
import { PagedResults } from 'app/models/paged-results';
import { RestService } from './rest-service';
import { Url_Base } from 'app/rest-constants';


@Injectable()
export class ReaderService extends BaseService {


  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService
  ) {
    super(router, authenticationService);
  }

  getUrl(): string {
    return `${Url_Base}/rfidservers/readers/`;
  }

  getNumberReaders(): Promise<Number> {
    const callUrl = this.getUrl() + 'count';
    return this.mRestService.get<Number>(callUrl);
  }

  getNumberReaderInstances(): Promise<Number> {
    const callUrl = this.getUrl() + 'instances/count';
    return this.mRestService.get<Number>(callUrl);
  }

  getAllReadersForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<Reader>> {
    const callUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<Reader>>(callUrl);
  }

  getAllReaderInstancesForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<ReaderInstance>> {
    const callUrl = this.getUrl() + 'instances/paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<ReaderInstance>>(callUrl);
  }


  getAllReaders(): Promise<Reader[]> {
    const callUrl = this.getUrl();
    return this.mRestService.get<Reader[]>(callUrl);
  }

  getReaderById(id: number): Promise<Reader> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<Reader>(callUrl);
  }

  getAllFunctions(): Promise<Function[]> {
    const rootUrl = this.getUrl() + 'functions';
    return this.mRestService.get<Function[]>(rootUrl);
  }

  createReader(serverObject: Reader): Promise<Reader> {
    const callUrl = this.getUrl();
    return this.mRestService.post<Reader>(callUrl, serverObject);
  }

  updateReader(serverObject: Reader): Promise<Reader> {
    const callUrl = this.getUrl();
    return this.mRestService.put<Reader>(callUrl, serverObject);
  }

  deleteReaders(ids: number[]): Promise<any> {
    const callUrl = this.getUrl();
    return this.mRestService.delete(callUrl, ids);
  }






  getAllReaderInstances(): Promise<ReaderInstance[]> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.get<ReaderInstance[]>(callUrl);
  }

  getReaderInstancesFiltered(start: number, size: number, order: string, filterString: string): Promise<ReaderInstance[]> {
    const url = this.getUrl() + `instances/filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<ReaderInstance[]>(url);
  } 

  getReaderInstanceById(id: number): Promise<ReaderInstance> {
    const callUrl = this.getUrl() + `instances/${id}`;
    return this.mRestService.get<ReaderInstance>(callUrl);
  }

  createReaderInstance(serverObjectInstance: ReaderInstance): Promise<ReaderInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.post<ReaderInstance>(callUrl, serverObjectInstance);
  }

  updateReaderInstance(serverObjectInstance: ReaderInstance): Promise<ReaderInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.put<ReaderInstance>(callUrl, serverObjectInstance);
  }

  setReaderInstancePosition(readerInstanceId: number, locationId: number, position: Point): Promise<ReaderInstance> {
    const callUrl = this.getUrl() + `instances/${readerInstanceId}/set-position`;
    const dto = { location_id: locationId, position: position };
    return this.mRestService.put<ReaderInstance>(callUrl, dto);
  }

  deleteReaderInstances(ids: number[]): Promise<any> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.delete(callUrl, ids)
      .then(response => { return null; });
  }

  /**
   * Resets the Reader's Driver and recreates it.
   */
  resetReaderDriver(id: number): Promise<void> {
    console.log(`Reset Reader Driver\n\tID: '${id}`);
    const callUrl = `${this.getUrl()}instances/${id}/reset-driver`;
    return this.mRestService.post(callUrl, null);
  }

  executeFunction(readerInstanceId: number, functionId: number, propertyPatches: PropertyPatch[], responseType?: string): Promise<any> {
    console.log(`Executing Reader Function\n\tReader Instance: '${readerInstanceId}'\n\tFunction ID: '${functionId}'\n\tProperty Patches: '${JSON.stringify(propertyPatches)}'\n\tResponse Type: '${responseType}'`);
    const callUrl = this.getUrl() + `instances/${readerInstanceId}/execute-function?function_id=${functionId}`;
    return this.mRestService.post(callUrl, propertyPatches, responseType);
  }

  executeFunctionByKey(readerInstanceId: number | string, functionKey: string, propertyPatches: PropertyPatch[]): Promise<any> {
    const callUrl = this.getUrl() + `instances/${readerInstanceId}/execute-function?function_key=${functionKey}`;
    return this.mRestService.post(callUrl, propertyPatches);
  }

  uploadReaders(formData: FormData): Promise<void> {
    const callUrl = this.getUrl() + 'data/upload';
    return this.mRestService.upload(callUrl, formData)
      .then(response => console.log(response));
  }

  downloadReaders(): Promise<Blob> {
    const callUrl = this.getUrl() + 'data/download';

    return this.mRestService.get(callUrl)
      .then(response => response['_body'] as Blob)
  }
}
